import { fromEvent } from 'rxjs';

import {
    findParent,
    focus,
    KeyboardNavigator as IKeyboardNavigator,
    KeyKeyCodeMap,
    KeyTypeMap,
    ResultAction,
} from '../common/keyboard.model';

declare let Zone;

export class KeyboardNavigator implements IKeyboardNavigator {
    private _lastFocused: HTMLElement;
    toggleNavigation: ($event: KeyboardEvent) => void;
    private _dropdownsShouldOpen: boolean;
    openDropdown: (id) => void;
    notificationBell: ($event: KeyboardEvent) => void;

    constructor(private readonly _root: HTMLElement) {
        // ngbDropdown autoClose = true
        // TODO unsubscribe
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const bindClick = () =>
            fromEvent(document, 'click').subscribe(() => {
                if (this._lastFocused) {
                    this._lastFocused.tabIndex = 0;
                    this._lastFocused = null;
                    // no return
                }
            });
        if (Zone) {
            Zone.root.run(() => bindClick());
        } else {
            bindClick();
        }
    }

    _handleEvent($event: KeyboardEvent): ResultAction {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const openLastFocusedDropdown = () => {
            let id = null;
            if (this._lastFocused && this._dropdownsShouldOpen) {
                id = this._lastFocused.parentElement.getAttribute('id'); // TODO revise
            }
            this.openDropdown(id);
        };

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const el = <HTMLElement>$event.target;
        if (!el) return ResultAction.IGNORE;

        if ($event.keyCode === KeyKeyCodeMap.Tab) {
            if ($event.type === KeyTypeMap.Down) {
                if (this._lastFocused) {
                    this._lastFocused.tabIndex = 0;
                    this._lastFocused = null;
                    // no return
                }
            }
            if ($event.type === KeyTypeMap.Up) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unnecessary-type-assertion
                const els = <HTMLElement[]>(
                    Array.from(
                        this._root.querySelectorAll(
                            '.nav-item > [role=menuitem]',
                        ),
                    )
                );
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                if (els.indexOf(el) !== -1) {
                    this._lastFocused = el;
                    // no return
                }
            }
            openLastFocusedDropdown();
            // no return
        }

        if ($event.type === KeyTypeMap.Up) return ResultAction.IGNORE;

        if (
            $event.keyCode === KeyKeyCodeMap.Enter ||
            $event.keyCode === KeyKeyCodeMap.Space
        ) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._lastFocused &&
                this._lastFocused.hasAttribute('aria-haspopup')
            ) {
                this._dropdownsShouldOpen = true;
                // no return
            }

            if (el.getAttribute('aria-controls') === 'navNavbar') {
                this.toggleNavigation($event);
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unnecessary-type-assertion
                const next = <HTMLElement>(
                    this._root.querySelector('#navNavbar [role=menuitem]')
                );
                focus(el, next);
                // no return
            }

            if (el.getAttribute('aria-controls') === 'notificationPanel') {
                this.notificationBell($event);
                // no return
            }
        }

        if ($event.keyCode === KeyKeyCodeMap.Escape) {
            this._dropdownsShouldOpen = false;
            // no return
            if (this._lastFocused) {
                focus(el, this._lastFocused);
                // no return
            }
        }

        if ($event.keyCode === KeyKeyCodeMap.ArrowUp) {
            return ResultAction.NO_DEFAULT;
        }

        if ($event.keyCode === KeyKeyCodeMap.ArrowDown) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._lastFocused &&
                this._lastFocused.hasAttribute('aria-haspopup')
            ) {
                this._dropdownsShouldOpen = true;
                // no return
            }
            if (this._lastFocused === el) {
                this._lastFocused.tabIndex = -1;
                // no return
            }
            return ResultAction.NO_DEFAULT;
        }

        if (
            $event.keyCode === KeyKeyCodeMap.ArrowLeft ||
            $event.keyCode === KeyKeyCodeMap.ArrowRight
        ) {
            const root = findParent(el, '[role=menubar]') || this._root;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unnecessary-type-assertion
            const els = <HTMLElement[]>(
                Array.from(root.querySelectorAll('.nav-item > [role=menuitem]'))
            );
            const index = els.indexOf(this._lastFocused || el);
            const goToPrevious = $event.keyCode === KeyKeyCodeMap.ArrowLeft;
            const nextIndex = index + (goToPrevious ? -1 : 1);
            const finalIndex =
                nextIndex >= els.length
                    ? 0
                    : nextIndex < 0
                    ? els.length - 1
                    : nextIndex;
            const next = els[finalIndex];
            focus(els, next);
            this._lastFocused = next;
            openLastFocusedDropdown();
            return ResultAction.BOTH;
        }

        return ResultAction.IGNORE;
    }

    handleEvent($event: KeyboardEvent): void {
        const ret = this._handleEvent($event);
        switch (ret) {
            case ResultAction.NO_PROPAGATE:
                $event.stopPropagation();
                break;
            case ResultAction.NO_DEFAULT:
                $event.preventDefault();
                break;
            case ResultAction.BOTH:
                $event.stopPropagation();
                $event.preventDefault();
        }
    }
}
